"use client";

import ImageCarousel from "@/components/ui/ImageCarousel";
import { getImageWithFallback } from "@/lib/default-image";
import Link from "next/link";
import { useState } from "react";
import QuickBook from "./QuickBook";
import PriceTag from "@/components/ui/PriceTag";

function Icon({
  name,
  className = "",
  fill = false,
}: {
  name: string;
  className?: string;
  fill?: boolean;
}) {
  return (
    <span className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}>
      {name}
    </span>
  );
}

export default function PropertyCard({
  p,
  locale,
  isAr,
}: {
  p: any;
  locale: string;
  isAr: boolean;
}) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const pid = p.slug || p._id || p.id;
  const title = isAr ? p.title?.ar : p.title?.en;
  const loc = (isAr ? p.region?.ar : p.region?.en) || p.location?.address || "";
  const images: string[] = (p.images || []).filter(Boolean);
  const [booking, setBooking] = useState(false);

  return (
    <>
      <Link
        href={`/${locale}/explore/${pid}`}
        className="group flex cursor-pointer flex-col border border-clay-100 rounded-3xl pb-4 bg-surface transition-shadow hover:shadow-[0_12px_32px_-12px_rgba(28,25,23,0.14)]"
      >
        <div className="relative isolate aspect-16/10 rounded-3xl overflow-hidden mb-4 shadow-sm">
          {/* All the unit's photos — swipe on phones, arrows on hover */}
          <ImageCarousel
            images={images}
            alt={title}
            isAr={isAr}
            className="h-full w-full"
            imgClassName="transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width:640px)100vw,(max-width:1024px)50vw,33vw"
          />
          {p.featured && (
            <div className="absolute top-4 inset-s-4 z-10 bg-white/90 backdrop-blur-md text-clay-600 px-3 py-1 rounded-full text-xs font-bold shadow-sm">
              {t("مميز", "Featured")}
            </div>
          )}
          {/* Public discount (dashboard «خصم عام») */}
          {p.discount?.active && (
            <div className="pointer-events-none absolute bottom-3 inset-s-3 z-10 rounded-full bg-red-600 px-2.5 py-1 text-xs font-extrabold text-white shadow-md">
              {t(`خصم ${p.discount.percent}%`, `${p.discount.percent}% off`)}
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col px-4">
          <div className="flex justify-between items-start mb-1 gap-2">
            <h3 className="font-bold text-lg text-ink-900 truncate">{title}</h3>
            {p.ratingCount > 0 ? (
              <div className="flex items-center gap-1 shrink-0" title={`${p.ratingCount} ${t("تقييم", "reviews")}`}>
                <Icon name="star" fill className="text-gold-500 text-[16px]" />
                <span className="text-sm font-bold">{Number(p.ratingAvg).toFixed(1)}</span>
                <span className="text-xs text-ink-500">({p.ratingCount})</span>
              </div>
            ) : null}
          </div>
          {p.unitCode && (
            <p className="mb-1">
              <span dir="ltr" className="inline-block rounded-md bg-clay-50 border border-clay-100 px-1.5 py-px font-mono text-[11px] font-semibold tracking-wide text-clay-700">
                {p.unitCode}
              </span>
            </p>
          )}
          {loc && <p className="text-sm text-ink-500 mb-3 truncate">{loc}</p>}
          <div className="flex items-center gap-4 text-xs text-ink-500 mb-4">
            <span className="flex items-center gap-1">
              <Icon name="bed" className="text-[16px]" /> {p.bedrooms} {t("غرف", "beds")}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="bathtub" className="text-[16px]" /> {p.bathrooms} {t("حمام", "baths")}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="group" className="text-[16px]" /> {p.guests} {t("ضيوف", "guests")}
            </span>
          </div>
          <div className="mt-auto flex items-center justify-between gap-3">
            <PriceTag p={p} isAr={isAr} showBadge={false} />
            {/* Book from the card — no need to open the unit page */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setBooking(true);
              }}
              className="shrink-0 rounded-xl bg-clay-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-clay-700 active:scale-[0.97]"
            >
              {t("اختر", "Select")}
            </button>
          </div>
        </div>
      </Link>

      {booking && (
        <QuickBook
          propertyId={pid}
          isAr={isAr}
          preview={{ title, image: getImageWithFallback(images[0] ?? null, "property"), unitCode: p.unitCode }}
          onClose={() => setBooking(false)}
        />
      )}
    </>
  );
}
